import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, mergeMap, of } from 'rxjs';
import { EntityModelLike, EntityTypeModelLike } from '../models/entity.model';
import { CacheFactoryService, ICacheService } from './cache-factory.service';

@Injectable()
export class EntityService {
  private cacheService: ICacheService;

  constructor(private http: HttpClient, cacheFactory: CacheFactoryService) {
    this.cacheService = cacheFactory.getOrCreate('entities');
  }

  listAllEntities() {
    return this.cacheService.getCached({
      path: `entityList`,
      source: () => {
        return this.http.get<EntityModelLike[]>(`~SiiaUtils/GetAllEntites`);
      },
    });
  }

  listAllEntityTypes() {
    return this.cacheService.getCached({
      path: `entityTypeList`,
      source: () => {
        return this.http.get<EntityTypeModelLike[]>(
          `~SiiaUtils/GetAllEntitiesTypes`
        );
      },
    });
  }

  listEntitiesByEntityType(entityTypeId: string) {
    if (!entityTypeId) {
      return of(null);
    }

    return this.cacheService.getCached({
      path: `entityTypeList/${entityTypeId}/entityList`,
      source: () => {
        return this.listAllEntities().pipe(
          map((list) => list?.filter((x) => x?.typeID === entityTypeId) ?? [])
        );
      },
    });
  }

  getEntityById(entityId: string) {
    if (!entityId) {
      return of(null);
    }

    return this.cacheService.getCached({
      path: `entityById/${entityId}`,
      source: () => {
        return this.listAllEntities().pipe(
          map((list) => list?.find((x) => x?.id === entityId))
        );
      },
    });
  }

  getEntityTypeById(typeId: string) {
    if (!typeId) {
      return of(null);
    }

    return this.cacheService.getCached({
      path: `entityTypeById/${typeId}`,
      source: () => {
        return this.listAllEntityTypes().pipe(
          map((list) => list?.find((x) => x?.id === typeId))
        );
      },
    });
  }

  getEntityTypeByEntityId(entityId: string) {
    if (!entityId) {
      return of(null);
    }

    return this.cacheService.getCached({
      path: `entityTypeByEntityId/${entityId}`,
      source: () => {
        return this.listAllEntities().pipe(
          map((list) => list?.find((x) => x?.id === entityId)?.typeID),
          mergeMap((entityTypeId) => {
            return this.listAllEntityTypes().pipe(
              map((list) => list?.find((x) => x?.id === entityTypeId))
            );
          })
        );
      },
    });
  }
}
